// ---------------------------------------------------------------------------
// StagePips — barra(s) de status reutilizavel(eis) para inputs e meses.
// ---------------------------------------------------------------------------
// Componente unico que renderiza pequenas "tracinhos" / "barrinhas"
// codificando o estado de uma etapa (parsing, xlinking, modelagem do
// mes, etc.). Centraliza:
//
//   - paleta de cores por estado (em ``COLORS`` no topo);
//   - animacoes (estado ``running`` = listras diagonais que andam;
//     estado ``loading`` = shimmer cinza claro);
//   - dimensoes (``SIZES``) para 2 variantes: ``input`` (3 traços
//     pequenos no rodape do InputBoxV3) e ``month`` (1 traco curto sob
//     o tab do MonthSelectorV3);
//
// Itere SOMENTE neste arquivo para mexer em cores/velocidade/tamanhos.
// Hot-reload do webpack atualiza sem reload da pagina.
// ---------------------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import cx from "classnames";

import {
  grayColor,
  successColor,
  dangerColor,
  warningColor,
} from "../../../../assets/jss/material-dashboard-pro-react";
import { PENDING_DECISION_HATCH_BY_SIZE } from "../../../../services/inputsV3StateRules";

// Hachurado de ``pending_decision`` — presets centralizados em
// ``inputsV3StateRules.js`` (input / month / dot).
const PENDING_DECISION_HATCHED = PENDING_DECISION_HATCH_BY_SIZE.input;

// ---------------------------------------------------------------------------
// Paleta do estado ``processing`` — derivada IDENTICAMENTE ao badge
// ``chipProcessing`` (``views/Modeling/inputs_components/styles.js``).
// O badge no topo da tela e os tracinhos do MonthSelector/Overview
// representam o MESMO conceito ("modelagem do mes em processamento")
// e portanto precisam compartilhar cores. Recomputamos aqui em vez de
// importar para evitar dependencia ciclica entre styles.js e
// StagePips. Se o badge mudar, ajustar aqui em paralelo.
// ---------------------------------------------------------------------------
const processingStripeDark = successColor[0];
const processingStripeLight = successColor[0] + "aa";

// ===========================================================================
//                         CONSTANTES — TWEAK AQUI
// ===========================================================================

// Cores base por estado canonico. Estados auxiliares (``done``,
// ``action``, etc.) sao mapeados para essas chaves no ``stateColor``.
export const COLORS = {
  success: successColor[3],
  // ``partial`` = xlinks parcialmente concluidos (alguns success, outros
  // pendentes) sem nenhum erro/warning. Verde mais claro que ``success``
  // para sinalizar "OK ate aqui mas ainda incompleto" sem alarmar com
  // amarelo. Visivel ao lado do ``success`` (#5cb860) — par escolhido
  // para contraste discreto, dentro da familia verde.
  partial: "#a5d6a7",
  // ``partial_warning`` = xlinks parcialmente concluidos com PELO
  // MENOS UM aviso. Espelho do ``partial`` (verde claro) na familia
  // laranja (Material orange 200, casado com Material green 200):
  // comunica "incompleto + ja existe sinal de atencao",
  // distinto de ``warning`` solido (amarelo intenso) reservado para
  // o aviso final ja consolidado quando todos os xlinks rodaram.
  partial_warning: "#ffcc80",
  warning: warningColor[2],
  error: dangerColor[2],
  // Hachurado amarelo+vermelho — mesma paleta usada no
  // ``OverviewV3`` (``INPUT_STATE_BACKGROUNDS.pending_decision``)
  // e na legenda dos inputs. Visualmente: "esta entre 'em
  // atencao' e 'rejeitado' — pipeline bloqueado aguardando
  // decisao humana".
  pending_decision: PENDING_DECISION_HATCHED,
  // Listras diagonais: ``running.primary`` e ``running.alternate``
  // alternam para criar o padrao. Mantenha-os proximos para evitar
  // contraste agressivo.
  running: {
    primary: "#5DA86E",
    alternate: "#7DC48D",
  },
  // Shimmer ``loading``: gradiente entre ``base`` e ``highlight`` que
  // desliza horizontalmente.
  loading: {
    base: "#c1c1c1",
    highlight: "#e6e6e6",
  },
  // Estados V3 estendidos (mes-level).
  stale: successColor[1],
  // ``system_error`` (D-35, leva 96; cor revisada na leva 99) — falha
  // tecnica do pipeline. Cor distinta do ``error`` (vermelho do
  // "rejeitado pelo cliente") para o usuario distinguir "preciso agir"
  // de "o sistema falhou". Preto comunica "interrompido / sem
  // alternativa" sem ambiguidade com o vermelho operacional. Espelha
  // a cor de ``MONTH_STATUS_LEGEND.system_error`` em
  // ``inputsV3StateRules.js`` e o gradiente preto de ``chipSystemError``
  // em ``inputs_components/styles.js``.
  system_error: "#000000",
  // ``processing`` agora compartilha a mesma paleta verde do badge
  // ``chipProcessing`` (PA-018). Antes era azul solido (#1565c0)
  // + listra branca, o que destoava do resto da app (azul nao e'
  // cor preferencial) e ficava com contraste muito alto na pequena
  // barra do MonthSelector. Par primary/alternate segue o mesmo
  // contrato de ``running`` para reusar ``pipBackgroundFor``.
  processing: {
    primary: processingStripeDark,
    alternate: processingStripeLight,
  },
  ready: "#f9a825",
  waiting: grayColor[5],
};

// Velocidade das animacoes. Ajuste para sentir "rapido"/"calmo".
//
// Onda 1.3 (mai/2026): ``runningDurationMs`` e ``stripeAngleDeg`` foram
// removidos. O efeito ``running`` foi unificado com o ``inFlight`` e
// agora usa ``IN_FLIGHT_DURATION_MS`` + gradiente horizontal (sem
// listras diagonais). ``stripePeriodPx`` continua sendo o
// ``backgroundSize`` dos pip-base (cell sem animacao) — manter aqui
// como tamanho-padrao de cada celula visual.
export const ANIMATION = {
  loadingDurationMs: 1400,
  stripePeriodPx: 8,
};

// ---------------------------------------------------------------------------
// IN-FLIGHT (Onda 1.8 — refeita)
// ---------------------------------------------------------------------------
// "Tem coisa processando agora neste input" — ex.: xlink rodando ou
// na fila depois do parse OK. O efeito e' o MESMO ripple/onda do
// chip "Processando" do header (linear-gradient com luz que desliza
// horizontalmente sobre a cor base), aplicado ao pip e ao donut.
//
// Cor base = a cor do estado da Camada 1 (verde claro, amarelo,
// vermelho, etc.). Cor "luz" = mesma cor clareada via ``color-mix``
// (suporte CSS nativo desde 2023). Decisao do usuario: NAO definir
// par ``{primary, alternate}`` por estado — usar uniformemente a
// versao clareada da cor base.
//
// Duracao = 1400ms (mesma que ``chipProcessingWave`` em
// ``inputs_components/styles.js``).
// ---------------------------------------------------------------------------

// Quanto de branco misturar para gerar a "luz" do shimmer. 40%
// e' visivelmente diferente da cor base sem perder identidade.
const IN_FLIGHT_LIGHTEN_PCT = 40;

// Duracao da onda. Mesmo valor do ``chipProcessingWave``.
const IN_FLIGHT_DURATION_MS = 1400;

/**
 * Gera o ``background`` CSS do ripple a partir de UMA cor base.
 * Funciona com qualquer formato CSS (hex, rgb, hsl, named, var()).
 * Exportada para o Donut do InputBoxV3 reusar (so precisa do par
 * de cores; o stroke do SVG aceita gradiente via <linearGradient>).
 */
export function pipBackgroundForInFlight(baseColor) {
  const lighter = `color-mix(in srgb, ${baseColor}, white ${IN_FLIGHT_LIGHTEN_PCT}%)`;
  return `linear-gradient(90deg, ${baseColor} 0%, ${lighter} 50%, ${baseColor} 100%)`;
}

/**
 * Versao clareada da cor base (helper para o gradiente SVG do
 * Donut, que precisa das duas cores em separado).
 */
export function lightenInFlightColor(baseColor) {
  return `color-mix(in srgb, ${baseColor}, white ${IN_FLIGHT_LIGHTEN_PCT}%)`;
}

/** Duracao em ms — exportada para o SVG do Donut sincronizar. */
export const IN_FLIGHT_RIPPLE_DURATION_MS = IN_FLIGHT_DURATION_MS;

// Dimensoes por variante. A variante e' escolhida via prop ``variant``.
export const SIZES = {
  input: {
    barHeight: 4, // espessura do tracinho
    barRadius: 2,
    barMaxWidth: 26, // limite do bar individual no InputBox
    gap: 4, // espacamento entre barras (3 stages)
    opacity: 0.7, // suaviza o bar quando o card nao esta hover
  },
  // ``inputSingle`` substitui os 3 tracinhos do rodape do InputBoxV3
  // por UM unico tracinho que sintetiza o estado agregado. Largura
  // igual ao donut do InputBoxV3 (68px) e altura mais presente porque
  // carrega sozinho a mensagem que antes era distribuida em 3 pips.
  // Tooltip continua expondo o detalhe por stage (ver InputBoxV3).
  inputSingle: {
    barHeight: 6,
    barRadius: 3,
    barMinWidth: 68,
    barMaxWidth: 68,
    gap: 0,
    opacity: 1,
  },
  month: {
    barHeight: 4,
    barRadius: 2,
    barMinWidth: 18, // tracinho do MonthSelector (largura fixa)
    barMaxWidth: 18,
    gap: 0,
    opacity: 1,
  },
};

// ===========================================================================
//                         RESOLUCAO DE ESTADOS
// ===========================================================================

// Mapeia aliases conhecidos para o vocabulario canonico desta paleta.
// Qualquer estado nao reconhecido cai em ``waiting``.
function canonicalState(state) {
  switch (state) {
    case "done":
      return "success";
    case "action":
      return "error";
    // ``system_error`` mantem identidade propria (D-35, leva 96) — vai
    // direto a uma chave dedicada em ``COLORS``. Antes colapsava em
    // ``error`` (vermelho operacional), o que apagava a distincao
    // entre "cliente precisa agir" e "pipeline falhou".
    case "system_error":
      return "system_error";
    case "in_progress":
      return "running";
    case "queued":
      return "running";
    // Onda 3.1 (mai/2026): backend e adapters passaram a emitir
    // ``"processing"`` como nome canonico do estado "executor rodando".
    // O vocabulario INTERNO deste componente continua usando
    // ``"running"`` (etiqueta visual/animacional historica); apenas
    // colapsamos a nova string para ele aqui.
    case "processing":
      return "running";
    case null:
    case undefined:
      return "waiting";
    default:
      return state;
  }
}

function isAnimatedRunning(state) {
  // Apos ``canonicalState`` colapsar ``"processing"`` em ``"running"``,
  // basta checar uma chave; a segunda comparacao fica como rede de
  // seguranca para callers que pulem o canonicalize.
  const c = canonicalState(state);
  return c === "running" || c === "processing";
}

function isLoadingShimmer(state) {
  return canonicalState(state) === "loading";
}

function flatColor(state, variant) {
  const c = canonicalState(state);
  if (c === "pending_decision") {
    if (variant === "month") return PENDING_DECISION_HATCH_BY_SIZE.month;
    return COLORS.pending_decision;
  }
  const v = COLORS[c];
  if (!v) return COLORS.waiting;
  if (typeof v === "string") return v;
  return v.primary || COLORS.waiting;
}

// Wrapper publico para consumidores externos que precisam de UMA cor
// solida representando um estado da paleta (ex.: caixinha quadrada do
// OverviewV3 reproduzindo a cor de um mes do seletor). NAO carrega
// animacao — quem precisa de listras (running) deve usar o
// componente ``StagePips`` diretamente.
export function stageFlatColor(state) {
  return flatColor(state, "input");
}

// ===========================================================================
//                           ESTILOS (JSS)
// ===========================================================================

const useStyles = makeStyles(() => ({
  // Container row (variante "input"). ``width: 100%`` e essencial:
  // sem ele o row colapsa para 0 dentro de um container flex pai
  // (ex.: ``InputBoxV3.footer`` com ``justifyContent: center``),
  // porque ``flex: 1`` dos pips so distribui espaco JA EXISTENTE.
  rowInput: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "3px",
    gap: `${SIZES.input.gap}px`,
  },
  rowMonth: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: `${SIZES.month.gap}px`,
  },
  // Container row (variante "inputSingle"). Mesmo layout do
  // ``rowInput`` (preenche a largura do footer + centraliza), mas
  // sem ``gap`` ja que so renderizamos 1 pip.
  rowInputSingle: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "3px",
  },
  // Pip base (variante "input").
  pipInput: {
    flex: 1,
    height: `${SIZES.input.barHeight}px`,
    borderRadius: `${SIZES.input.barRadius}px`,
    maxWidth: `${SIZES.input.barMaxWidth}px`,
    opacity: SIZES.input.opacity,
    backgroundSize: `${ANIMATION.stripePeriodPx}px ${ANIMATION.stripePeriodPx}px`,
  },
  // Pip base (variante "inputSingle"). Largura fixa (52px) e altura
  // 5px — mais prominente que os 3 pips antigos porque carrega
  // sozinho o sinal sintese do card.
  pipInputSingle: {
    height: `${SIZES.inputSingle.barHeight}px`,
    borderRadius: `${SIZES.inputSingle.barRadius}px`,
    minWidth: `${SIZES.inputSingle.barMinWidth}px`,
    maxWidth: `${SIZES.inputSingle.barMaxWidth}px`,
    opacity: SIZES.inputSingle.opacity,
    backgroundSize: `${ANIMATION.stripePeriodPx}px ${ANIMATION.stripePeriodPx}px`,
  },
  // Pip base (variante "month").
  pipMonth: {
    height: `${SIZES.month.barHeight}px`,
    borderRadius: `${SIZES.month.barRadius}px`,
    minWidth: `${SIZES.month.barMinWidth}px`,
    maxWidth: `${SIZES.month.barMaxWidth}px`,
    opacity: SIZES.month.opacity,
    backgroundSize: `${ANIMATION.stripePeriodPx}px ${ANIMATION.stripePeriodPx}px`,
  },
  // Estado ``running`` / ``processing`` — Onda 1.3 unifica este efeito
  // com o do ``inFlight``: ripple horizontal (gradient ``base -> luz
  // -> base``) deslizando via ``backgroundPosition``. Sobrescreve o
  // ``backgroundSize`` herdado do pip base (que era do periodo da
  // listra 45deg legada) para 200%/100% — mesmo perfil do ripple.
  running: {
    backgroundSize: "200% 100%",
    animation: `$pipsRippleWave ${IN_FLIGHT_DURATION_MS}ms ease-in-out infinite`,
  },
  // Estado ``loading`` — shimmer claro (sem cor de status; usado
  // enquanto o backend ainda nao publicou agregados).
  loading: {
    background: `linear-gradient(90deg, ${COLORS.loading.base} 0%, ${COLORS.loading.highlight} 50%, ${COLORS.loading.base} 100%)`,
    backgroundSize: "200% 100%",
    animation: `$pipsLoadingShimmer ${ANIMATION.loadingDurationMs}ms ease-in-out infinite`,
  },
  // Onda 1.3: ``@keyframes pipsRunningStripeMove`` removida — o
  // estado ``running`` agora reusa ``pipsRippleWave`` (mesma animacao
  // do ``inFlight``), entao a keyframe legada ficou orfa.
  "@keyframes pipsLoadingShimmer": {
    "0%": { backgroundPosition: "200% 0" },
    "100%": { backgroundPosition: "-200% 0" },
  },
  // ---- Onda 1.8 (refeita): "in-flight" = ripple/onda --------------
  // Mesmo perfil do ``chipProcessingWave`` usado no chip
  // "Processando" do header (cf. ``inputs_components/styles.js``).
  // Anima ``backgroundPosition`` em um gradiente
  // ``base -> luz -> base`` com ``backgroundSize: 200% 100%`` para
  // que a "luz" deslize horizontalmente sobre a cor da Camada 1.
  // O ``background`` em si e' setado inline em ``Pip`` (depende da
  // cor base do estado).
  inFlight: {
    backgroundSize: "200% 100%",
    animation: `$pipsRippleWave ${IN_FLIGHT_DURATION_MS}ms ease-in-out infinite`,
  },
  "@keyframes pipsRippleWave": {
    "0%": { backgroundPosition: "200% 0" },
    "100%": { backgroundPosition: "-200% 0" },
  },
}));

// ===========================================================================
//                            PRIMITIVA: <Pip />
// ===========================================================================
//
// Renderiza UM tracinho codificando UM estado. Calcula classes/inline
// styles com base na variante.

function pipBackgroundFor(state, variant) {
  if (isAnimatedRunning(state)) {
    const c = canonicalState(state);
    const primary = COLORS[c].primary;
    return pipBackgroundForInFlight(primary);
  }
  return flatColor(state, variant);
}

function Pip({ state, variant, inFlight }) {
  const c = useStyles();
  const baseClass =
    variant === "month"
      ? c.pipMonth
      : variant === "inputSingle"
        ? c.pipInputSingle
        : c.pipInput;
  const isRun = isAnimatedRunning(state);
  const isLoading = isLoadingShimmer(state);
  // Onda 1.8 (refeita): ``inFlight`` substitui o background liso por
  // um gradiente ``base -> luz -> base`` animado horizontalmente
  // (ripple). Aplicado APENAS quando a cor base e' solida — se o
  // pip ja esta com listras animadas (running/processing) ou com
  // shimmer de loading, manter o efeito original (sobrepor ripple
  // sobre stripe animado vira ruido visual).
  const inFlightBlocked = isRun || isLoading;
  const useInFlight = inFlight && !inFlightBlocked;

  // Quando ``useInFlight``, usamos ``backgroundImage`` (nao
  // ``background`` shorthand) para nao apagar o ``backgroundSize:
  // 200% 100%`` definido na classe ``inFlight`` — necessario para a
  // animacao do ripple deslizar.
  let inlineStyle;
  if (isLoading) {
    inlineStyle = undefined;
  } else if (useInFlight) {
    inlineStyle = {
      backgroundImage: pipBackgroundForInFlight(flatColor(state, variant)),
    };
  } else {
    inlineStyle = { background: pipBackgroundFor(state, variant) };
  }

  return (
    <div
      className={cx(baseClass, {
        [c.running]: isRun,
        [c.loading]: isLoading,
        [c.inFlight]: useInFlight,
      })}
      style={inlineStyle}
    />
  );
}
Pip.propTypes = {
  state: PropTypes.string,
  variant: PropTypes.oneOf(["input", "inputSingle", "month"]).isRequired,
  inFlight: PropTypes.bool,
};
Pip.defaultProps = {
  inFlight: false,
};

// ===========================================================================
//                          COMPONENTE PUBLICO
// ===========================================================================
//
// ``StagePips`` recebe uma lista de estados e renderiza um pip por
// estado. ``variant="input"`` para os 3 tracinhos do InputBoxV3;
// ``variant="month"`` para 1 tracinho do MonthSelectorV3.
//
// Exemplos:
//   <StagePips states={["success", "running", "waiting"]} variant="input" />
//   <StagePips states={["loading"]} variant="month" />

const StagePips = ({ states, variant, inFlight }) => {
  const c = useStyles();
  const list = Array.isArray(states) ? states : [states];
  const rowClass =
    variant === "month"
      ? c.rowMonth
      : variant === "inputSingle"
        ? c.rowInputSingle
        : c.rowInput;
  return (
    <div className={rowClass}>
      {list.map((s, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Pip key={i} state={s} variant={variant} inFlight={inFlight} />
      ))}
    </div>
  );
};

StagePips.propTypes = {
  states: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  variant: PropTypes.oneOf(["input", "inputSingle", "month"]),
  // Onda 1.8 (refeita): ``inFlight`` substitui a cor lisa por um
  // ripple/onda sobre a propria cor base do estado. Quem dispara e'
  // a flag ``hasInFlightOverlay`` calculada em ``classifyInputPip``
  // (cf. inputsV3StateRules.js).
  inFlight: PropTypes.bool,
};

StagePips.defaultProps = {
  variant: "input",
  inFlight: false,
};

export default StagePips;
